// uiarc: toast-stack (https://uiarc.dev/r/toast-stack.json) — CSS Modules yerine Tailwind; tek `toast` bileşeninin
// yerini alır (uiarc'ta ikisi de aynı kartı çizer, yığın kuyruk + güncelleme + eylem ekler).
// Yeni bildirimler alt kenardan yükselir, eskileri arkaya yığılır (her biri 14px görünür, kademeli küçülür); yığına
// gelince/odaklanınca liste olarak açılır. Yığın açıkken ya da sekme gizliyken süre durur; sağa kaydırarak
// kapatılır (hız taşınır), kısa kaydırma yayla geri döner. Aynı `id` ile `update` kartı yerinde dönüştürür.
// Sapmalar: metinler `messages.toast`, vurgu = marka yeşili, z-sıra tooltip'in (90) üstünde.
// Kullanım: `ToastStackProvider` + tek `ToastStack` (providers.tsx), her yerde `useToastStack().toast({...})`.
import { CircleCheck, CircleX, Info, TriangleAlert, X } from 'lucide-react'
import {
  AnimatePresence,
  animate,
  motion,
  usePresence,
  useMotionValue,
  useReducedMotion,
  useTransform,
  type MotionProps,
  type Transition,
} from 'motion/react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type FocusEvent,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react'

import {
  BORDER,
  createToastStore,
  layoutStack,
  SWIPE_FADE,
  type Target,
  type ToastRecord,
  type ToastStackApi,
  type ToastStackProviderProps,
  type ToastStore,
} from '@/components/feedback/toast-store'
import { iconIn, shown } from '@/components/ui/morph-copy-motion'
import { Swap } from '@/components/ui/morph-copy'
import { messages } from '@/lib/messages'
import { motionTokens } from '@/lib/motion'
import { cn } from '@/lib/utils'

export type {
  ToastAction,
  ToastOptions,
  ToastStackApi,
  ToastStackProviderProps,
  ToastType,
} from '@/components/feedback/toast-store'

export interface ToastStackProps {
  /** Bildirim bölgesinin erişilebilir adı. */
  label?: string
  position?: 'bottom-right' | 'bottom-center' | 'bottom-left'
  /** Yığını pencere yerine en yakın konumlu atanın içine sabitler (paneller ve önizlemeler için). */
  contained?: boolean
  /** Aynı anda görünen bildirim sayısı. Eskileri arkada bekler, öndekiler kapandıkça geri gelir. */
  visibleToasts?: number
  /** Alt+T odağı yığına taşır. */
  hotkey?: boolean
  className?: string
}

const EMPTY: ToastRecord[] = []
const icons = { success: CircleCheck, info: Info, warning: TriangleAlert, error: CircleX }

const { duration, ease, spring, blur } = motionTokens
const fade: Transition = { duration: duration.fast, ease: ease.standard }
const enterFade: Transition = { duration: duration.standard, ease: ease.enter }
const reducedFade: Transition = { duration: duration.fast, ease: ease.standard }
const exitFast: Transition = { duration: duration.fast, ease: ease.standard }
const textIn = { opacity: 0, y: '0.3em', filter: `blur(${blur.soft}px)` }
const textOut = {
  opacity: 0,
  y: '-0.3em',
  filter: `blur(${blur.subtle}px)`,
  transition: exitFast,
}
const fadeOnly: MotionProps = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0, transition: reducedFade },
  transition: reducedFade,
}
const textSwapMotion: MotionProps = {
  initial: textIn,
  animate: shown,
  exit: textOut,
  transition: { duration: duration.standard, ease: ease.enter },
}
/** Ölçek yayda, solma ve bulanıklık süreyle; bulanıklık sıfırın altına taşmaz. */
const iconSwapMotion: MotionProps = {
  initial: iconIn,
  animate: shown,
  exit: { ...iconIn, transition: exitFast },
  transition: { ...spring.snappy, opacity: fade, filter: fade },
}

/** Bu hızla bırakılan bir hareketin duracağı yer (hızlı kaydırma yavaşlaması). */
const project = (velocity: number) => ((velocity / 1000) * 0.99) / (1 - 0.99)
/** Kenar ötesi direnç: çekiş sürer ama azalarak, kaydırma görünümünün taşması gibi. */
const rubberBand = (distance: number, dimension: number) =>
  (1 - 1 / ((distance * 0.55) / dimension + 1)) * dimension
const subscribeVisibility = (listener: () => void) => {
  document.addEventListener('visibilitychange', listener)
  return () => document.removeEventListener('visibilitychange', listener)
}
const getServerSnapshot = () => EMPTY
const focusVisible = (element: Element) => {
  try {
    return element.matches(':focus-visible')
  } catch {
    return true
  }
}

const ToastStackContext = createContext<ToastStore | null>(null)

function useStore() {
  const store = useContext(ToastStackContext)
  if (!store) throw new Error('Toast tüketicileri <ToastStackProvider> içinde olmalı.')
  return store
}

/** Bildirim kuyruğunu alt ağacına bağlar; aynı sayfadaki iki yığın bildirim paylaşmaz. */
export function ToastStackProvider({
  children,
  duration = 5000,
  limit = 12,
}: ToastStackProviderProps) {
  const [store] = useState(() => createToastStore(duration, limit))
  return <ToastStackContext.Provider value={store}>{children}</ToastStackContext.Provider>
}

/** Sağlayıcı içinde her yerden bildirim gösterir, günceller, kapatır. `count` kuyruktaki bildirim sayısıdır. */
export function useToastStack(): ToastStackApi & { count: number } {
  const store = useStore()
  const count = useSyncExternalStore(
    store.subscribe,
    () => store.getSnapshot().length,
    () => 0,
  )
  return { toast: store.toast, update: store.update, dismiss: store.dismiss, count }
}

type Gesture = {
  pointerId: number
  startX: number
  startY: number
  origin: number
  active: boolean
  samples: { t: number; x: number }[]
}

interface ToastItemProps {
  toast: ToastRecord
  target: Target
  expanded: boolean
  front: boolean
  hidden: boolean
  paused: boolean
  reduce: boolean
  store: ToastStore
  onMeasure: (id: string, height: number) => void
  onDragChange: (dragging: boolean) => void
  onTap: () => void
  onHandOff: (leaving: HTMLElement, keyboard: boolean) => void
}

const roundButton =
  'rounded-pill flex-none cursor-pointer [-webkit-tap-highlight-color:transparent] [transition:scale_var(--duration-spring)_var(--ease-spring),background-color_var(--duration-fast)_var(--ease-standard),border-color_var(--duration-fast)_var(--ease-standard),color_var(--duration-fast)_var(--ease-standard)] active:scale-[0.96] active:[transition-duration:var(--duration-instant)] active:[transition-timing-function:var(--ease-standard)] motion-reduce:transition-none motion-reduce:active:scale-100'

function ToastItem({
  toast,
  target,
  expanded,
  front,
  hidden,
  paused,
  reduce,
  store,
  onMeasure,
  onDragChange,
  onTap,
  onHandOff,
}: ToastItemProps) {
  const { id } = toast
  const [isPresent, safeToRemove] = usePresence()
  const itemRef = useRef<HTMLLIElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const actionRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  // Yığın geometrisi hareket değerlerinde durur; yığını, parmağı ya da zamanlayıcıyı izlemek kare başına yeniden çizdirmez.
  const y = useMotionValue(0)
  const scale = useMotionValue(1)
  const height = useMotionValue(0)
  const opacity = useMotionValue(0)
  const contentOpacity = useMotionValue(1)
  const x = useMotionValue(0)
  const swipeFade = useTransform(x, (value) =>
    value > 0 ? 1 - Math.min(value / SWIPE_FADE, 1) * 0.75 : 1,
  )
  /** Son hedefin uygulandığı açık durumu; bildirim girene dek null. */
  const applied = useRef<boolean | null>(null)
  const swiped = useRef(false)
  const remaining = useRef(toast.duration)
  const gesture = useRef<Gesture | null>(null)
  const suppressClick = useRef(false)
  const pointerType = useRef('mouse')

  // İlk boyamadan önce ölç; bildirim tam kendi yüksekliği kadar kenarın altından girer.
  useLayoutEffect(() => {
    const node = contentRef.current
    if (!node) return
    onMeasure(id, node.offsetHeight + BORDER)
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => onMeasure(id, node.offsetHeight + BORDER))
    observer.observe(node)
    return () => observer.disconnect()
  }, [id, onMeasure])

  // Her yığın değişimi çalışan yayları bulundukları yerden yeniden hedefler; hızlı bildirimler ve üzerine gelmeler kuyruklanmaz.
  useLayoutEffect(() => {
    if (!isPresent || target.height <= 0) return
    const entering = applied.current === null
    const morph = !entering && applied.current !== expanded
    applied.current = expanded
    if (reduce) {
      y.jump(target.y)
      scale.jump(target.scale)
      height.jump(target.height)
      animate(opacity, target.opacity, reducedFade)
      animate(contentOpacity, target.content, reducedFade)
      return
    }
    if (entering) {
      y.jump(target.y + target.height)
      scale.jump(target.scale)
      height.jump(target.height)
      contentOpacity.jump(target.content)
      opacity.jump(0)
    }
    const spr = morph ? spring.morph : spring.smooth
    animate(y, target.y, spr)
    animate(scale, target.scale, spr)
    if (!entering) animate(height, target.height, spr)
    animate(opacity, target.opacity, entering ? enterFade : fade)
    animate(contentOpacity, target.content, fade)
  }, [
    contentOpacity,
    expanded,
    height,
    isPresent,
    opacity,
    reduce,
    scale,
    target.content,
    target.height,
    target.opacity,
    target.scale,
    target.y,
    y,
  ])

  // Güncelleme saati yeniden başlatır; duraklatma kalan süreyi korur.
  useEffect(() => {
    remaining.current = toast.duration
  }, [toast.version, toast.duration])
  useEffect(() => {
    if (!isPresent || paused || !Number.isFinite(remaining.current) || toast.duration <= 0) return
    const started = performance.now()
    const timer = window.setTimeout(() => store.dismiss(id), Math.max(remaining.current, 0))
    return () => {
      window.clearTimeout(timer)
      remaining.current -= performance.now() - started
    }
  }, [id, isPresent, paused, store, toast.version, toast.duration])

  // Ayrılırken: bildirim etkisizleşmeden odağı devret, sonra hızla sol. Kapatılan kenara batar; fırlatılan uçmaya devam eder.
  useEffect(() => {
    if (isPresent) return
    const node = itemRef.current
    if (node) {
      const active = document.activeElement
      if (active instanceof HTMLElement && node.contains(active))
        onHandOff(node, focusVisible(active))
      node.setAttribute('inert', '')
    }
    const done = () => safeToRemove?.()
    if (reduce) {
      animate(opacity, 0, reducedFade).then(done)
      return
    }
    if (!swiped.current) {
      animate(y, y.get() + 10, spring.smooth)
      animate(scale, scale.get() * 0.96, spring.smooth)
    }
    animate(opacity, 0, {
      duration: swiped.current ? 0.22 : duration.exit,
      ease: ease.standard,
    }).then(done)
  }, [isPresent, onHandOff, opacity, reduce, safeToRemove, scale, y])

  function onPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    pointerType.current = event.pointerType
    suppressClick.current = false
    if (event.button !== 0 || !isPresent || (event.target as Element).closest('button, a')) return
    // Hâlâ geri yaylanan bildirimi tam bulunduğu yerden yakala.
    x.stop()
    gesture.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      origin: x.get(),
      active: false,
      samples: [],
    }
  }

  function onPointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    const current = gesture.current
    if (!current || current.pointerId !== event.pointerId) return
    if (!current.active) {
      const dx = event.clientX - current.startX
      const dy = event.clientY - current.startY
      if (Math.hypot(dx, dy) < 6) return
      if (Math.abs(dy) > Math.abs(dx)) {
        gesture.current = null
        return
      }
      // Buradan izlemeye başla; eşiği geçmek bildirimi sıçratmaz.
      current.active = true
      current.startX = event.clientX
      suppressClick.current = true
      event.currentTarget.setPointerCapture(event.pointerId)
      event.currentTarget.dataset.dragging = ''
      window.getSelection()?.removeAllRanges()
      onDragChange(true)
    }
    const raw = current.origin + event.clientX - current.startX
    // Sağ parmağı 1:1 izler; sol yanlış yön, kaydırma taşması gibi direnir.
    x.set(raw >= 0 ? raw : -rubberBand(-raw, event.currentTarget.offsetWidth))
    current.samples.push({ t: event.timeStamp, x: raw })
    while (current.samples.length > 2 && event.timeStamp - current.samples[0]!.t > 100)
      current.samples.shift()
  }

  function onPointerEnd(event: ReactPointerEvent<HTMLDivElement>, cancelled: boolean) {
    const current = gesture.current
    if (!current || current.pointerId !== event.pointerId) return
    gesture.current = null
    if (!current.active) return
    delete event.currentTarget.dataset.dragging
    onDragChange(false)
    const first = current.samples[0]
    const last = current.samples[current.samples.length - 1]
    // Kalkmadan önce duran parmak fırlatma taşımaz.
    const velocity =
      cancelled || !first || !last || last.t === first.t || event.timeStamp - last.t > 60
        ? 0
        : ((last.x - first.x) / (last.t - first.t)) * 1000
    const width = event.currentTarget.offsetWidth
    const offset = x.get()
    if (!cancelled && offset > 0 && velocity > -200 && offset + project(velocity) > width * 0.4) {
      swiped.current = true
      if (!reduce)
        animate(x, width + 40, {
          type: 'spring',
          visualDuration: 0.32,
          bounce: 0,
          velocity: Math.max(velocity, 0),
        })
      store.dismiss(id)
      return
    }
    if (reduce) x.jump(0)
    else animate(x, 0, { ...spring.snappy, velocity: offset < 0 ? velocity * 0.3 : velocity })
  }

  function onCardClick(event: MouseEvent<HTMLDivElement>) {
    if (suppressClick.current) {
      suppressClick.current = false
      return
    }
    // Dokunmada üzerine gelme yoktur; karta dokunmak yığını açar ya da kapatır.
    if (pointerType.current !== 'mouse' && !(event.target as Element).closest('button, a')) onTap()
  }

  // Bildirimini güncelleyen eylem çoğu zaman kendini toplar; klavye odağı sayfaya değil kapat düğmesine iner.
  function runAction(event: MouseEvent<HTMLButtonElement>) {
    const keyboard = focusVisible(event.currentTarget)
    store.runAction(id)
    if (keyboard && !store.getSnapshot().find((item) => item.id === id)?.action)
      closeRef.current?.focus()
  }

  function onKeyDown(event: KeyboardEvent<HTMLLIElement>) {
    if (event.key !== 'Escape') return
    event.stopPropagation()
    store.dismiss(id)
  }

  const markMoving = (moving: boolean) => {
    if (actionRef.current) actionRef.current.dataset.moving = moving ? 'true' : 'false'
  }
  const Icon = toast.type === 'loading' ? null : icons[toast.type]
  const swap = reduce ? fadeOnly : textSwapMotion

  return (
    <motion.li
      ref={itemRef}
      className="group/item absolute inset-x-0 bottom-0 origin-[50%_0]"
      style={{ y, scale, height, opacity, zIndex: toast.seq }}
      data-front={front}
      data-expanded={expanded}
      inert={hidden}
      onKeyDown={onKeyDown}
    >
      <motion.div
        className={cn(
          'border-border bg-surface-raised text-foreground rounded-panel shadow-raised @container absolute inset-0 touch-pan-y overflow-hidden border [-webkit-tap-highlight-color:transparent]',
          '[transition:background-color_var(--duration-standard)_var(--ease-standard),border-color_var(--duration-standard)_var(--ease-standard)]',
          'group-data-[expanded=true]/item:shadow-floating group-data-[front=true]/item:shadow-floating',
          // Arkadaki bildirim ölçekle birlikte tonla da bir adım geride durur.
          'group-data-[expanded=false]/item:group-data-[front=false]/item:bg-surface-muted',
          'data-[dragging]:cursor-grabbing data-[dragging]:select-none motion-reduce:transition-none',
        )}
        style={{ x, opacity: swipeFade }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={(event) => onPointerEnd(event, false)}
        onPointerCancel={(event) => onPointerEnd(event, true)}
        onClick={onCardClick}
      >
        <motion.div
          ref={contentRef}
          className="flex items-start gap-3 py-3 pr-3 pl-4 @max-[21rem]:flex-wrap @max-[21rem]:gap-y-2"
          style={{ opacity: contentOpacity }}
        >
          {/* Yeni tür simgeyi yerinde dönüştürür: eski işaret bulanıklığa küçülürken yenisi içinden büyür. */}
          <span
            className={cn(
              'relative grid h-5 w-4.5 flex-none place-items-center transition-colors duration-(--duration-fast) ease-(--ease-standard) motion-reduce:transition-none',
              toast.type === 'success' && 'text-success',
              toast.type === 'info' && 'text-brand',
              toast.type === 'warning' && 'text-warning',
              toast.type === 'error' && 'text-danger',
              toast.type === 'loading' && 'text-text-muted',
            )}
            data-type={toast.type}
            aria-hidden="true"
          >
            <AnimatePresence mode="popLayout" initial={false}>
              <Swap
                key={toast.type}
                className="col-start-1 row-start-1 grid place-items-center"
                {...(reduce ? fadeOnly : iconSwapMotion)}
              >
                {Icon ? (
                  <Icon className="block size-4.5" strokeWidth={1.75} />
                ) : (
                  <span className="block size-[15px] animate-[spin_.8s_linear_infinite] rounded-full border-[1.75px] border-current border-r-transparent motion-reduce:animate-none" />
                )}
              </Swap>
            </AnimatePresence>
          </span>
          <div className="relative min-w-0 flex-1 text-sm [line-height:20px] tracking-(--tracking-body)">
            <span className="sr-only">{messages.toast.types[toast.type]}: </span>
            <span className="relative block font-medium">
              <AnimatePresence mode="popLayout" initial={false}>
                <Swap key={toast.title} className="block" {...swap}>
                  {toast.title}
                </Swap>
              </AnimatePresence>
            </span>
            <AnimatePresence mode="popLayout" initial={false}>
              {toast.description ? (
                <Swap
                  key={toast.description}
                  className="text-text-secondary mt-0.5 block"
                  {...swap}
                >
                  {toast.description}
                </Swap>
              ) : null}
            </AnimatePresence>
          </div>
          {/* Güncelleme eylemi kaldırınca genişliği kıvrılır; metin sıçramadan genişler. */}
          <AnimatePresence initial={false}>
            {toast.action ? (
              <motion.div
                key="action"
                ref={actionRef}
                className="-mt-1 flex flex-none justify-end data-[moving=true]:overflow-hidden @max-[21rem]:order-1 @max-[21rem]:mt-0 @max-[21rem]:basis-full @max-[21rem]:justify-start @max-[21rem]:pl-[calc(18px+0.75rem)]"
                initial={reduce ? { opacity: 0 } : { width: 0, opacity: 0 }}
                animate={{ width: 'auto', opacity: 1 }}
                exit={reduce ? { opacity: 0 } : { width: 0, opacity: 0 }}
                transition={reduce ? reducedFade : { width: spring.smooth, opacity: fade }}
                onAnimationStart={() => markMoving(true)}
                onAnimationComplete={() => markMoving(false)}
              >
                <button
                  type="button"
                  className={cn(
                    roundButton,
                    'border-border bg-surface text-foreground hover:border-border-strong hover:bg-surface-muted h-7 border px-3 text-sm font-medium whitespace-nowrap',
                  )}
                  onClick={runAction}
                >
                  {toast.action.label}
                </button>
              </motion.div>
            ) : null}
          </AnimatePresence>
          <button
            ref={closeRef}
            type="button"
            className={cn(
              roundButton,
              'text-text-muted hover:bg-surface-muted hover:text-foreground -mt-1 -mr-0.5 grid size-7 place-items-center border border-transparent bg-transparent',
            )}
            aria-label={messages.toast.dismiss}
            onClick={() => store.dismiss(id)}
          >
            <X className="size-4" strokeWidth={1.75} aria-hidden="true" />
          </button>
        </motion.div>
      </motion.div>
    </motion.li>
  )
}

const positions = {
  'bottom-right': 'right-6',
  'bottom-left': 'left-6',
  'bottom-center': 'inset-x-0 mx-auto',
} as const
const containedPositions = {
  'bottom-right': 'right-4',
  'bottom-left': 'left-4',
  'bottom-center': 'inset-x-0 mx-auto',
} as const

export function ToastStack({
  label = messages.toast.region,
  position = 'bottom-right',
  contained = false,
  visibleToasts = 3,
  hotkey = true,
  className,
}: ToastStackProps) {
  const store = useStore()
  const toasts = useSyncExternalStore(store.subscribe, store.getSnapshot, getServerSnapshot)
  const reduce = !!useReducedMotion()
  const pageHidden = useSyncExternalStore(
    subscribeVisibility,
    () => document.visibilityState === 'hidden',
    () => false,
  )
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  const [tapped, setTapped] = useState(false)
  const [dragging, setDragging] = useState(false)
  const [heights, setHeights] = useState<Record<string, number>>({})
  const regionRef = useRef<HTMLElement>(null)
  const listRef = useRef<HTMLOListElement>(null)
  const returnFocus = useRef<HTMLElement | null>(null)
  if (tapped && toasts.length === 0) setTapped(false)
  const expanded = toasts.length > 0 && (hovered || focused || tapped)
  const paused = expanded || dragging || pageHidden

  const measure = useCallback(
    (id: string, height: number) =>
      setHeights((current) => (current[id] === height ? current : { ...current, [id]: height })),
    [],
  )
  const prune = useCallback(
    () =>
      setHeights((current) =>
        Object.fromEntries(
          store
            .getSnapshot()
            .filter((item) => item.id in current)
            .map((item) => [item.id, current[item.id]!]),
        ),
      ),
    [store],
  )
  const toggleTap = useCallback(() => setTapped((open) => !open), [])

  /** Klavye kullanıcısı sıradaki bildirime iner; hiç kalmadıysa yığına girmeden önceki yere döner. */
  const handOff = useCallback((leaving: HTMLElement, keyboard: boolean) => {
    const items = Array.from(listRef.current?.children ?? []) as HTMLElement[]
    const at = items.indexOf(leaving)
    const candidates = items.filter((item) => item !== leaving && !item.hasAttribute('inert'))
    const next =
      candidates.find((item) => items.indexOf(item) > at) ?? candidates[candidates.length - 1]
    const button = next?.querySelector<HTMLElement>('button')
    if (keyboard && button) button.focus()
    else if (keyboard && returnFocus.current?.isConnected) returnFocus.current.focus()
    else (document.activeElement as HTMLElement | null)?.blur()
  }, [])

  useEffect(() => {
    if (!hotkey) return
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (!event.altKey || event.metaKey || event.ctrlKey || event.code !== 'KeyT') return
      const target = listRef.current?.querySelector<HTMLElement>(':scope > li:not([inert]) button')
      if (!target) return
      event.preventDefault()
      // Tarayıcılar değiştirici kısayoldan sonra odak halkasını atlar; kısayol onu ister ve yığını kendisi açar.
      target.focus({ focusVisible: true } as FocusOptions)
      setFocused(true)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [hotkey])

  useEffect(() => {
    if (!tapped) return
    const onDown = (event: PointerEvent) => {
      if (!regionRef.current?.contains(event.target as Node)) setTapped(false)
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [tapped])

  const { targets, listHeight } = layoutStack(toasts, heights, expanded, visibleToasts)

  function onFocus(event: FocusEvent<HTMLElement>) {
    const from = event.relatedTarget
    if (!(from instanceof Node) || !event.currentTarget.contains(from))
      returnFocus.current = from instanceof HTMLElement ? from : null
    // Yalnız klavye odağı yığını açar; fareyle bir düğmeye tıklamak onu açık tutmamalı.
    if (focusVisible(event.target)) setFocused(true)
  }

  function onBlur(event: FocusEvent<HTMLElement>) {
    if (
      !(event.relatedTarget instanceof Node) ||
      !event.currentTarget.contains(event.relatedTarget)
    )
      setFocused(false)
  }

  return (
    <section
      ref={regionRef}
      data-slot="toast-stack"
      className={cn(
        // Bölge yalnız listeyi taşır; bildirimlerin dışındaki tıklamaları engellemez. Z-sıra tooltip'in (90) üstünde.
        'pointer-events-none w-[min(22.5rem,calc(100%-2rem))]',
        contained
          ? ['absolute bottom-4 z-[2]', containedPositions[position]]
          : [
              'fixed bottom-6 z-100',
              positions[position],
              'max-[30rem]:inset-x-0 max-[30rem]:bottom-4 max-[30rem]:mx-auto',
            ],
        className,
      )}
      aria-label={hotkey ? `${label} (Alt+T)` : label}
      aria-live="polite"
      aria-relevant="additions text"
      aria-atomic="false"
      onFocus={onFocus}
      onBlur={onBlur}
    >
      <ol
        ref={listRef}
        className="pointer-events-auto relative m-0 list-none p-0"
        style={{ height: listHeight }}
        data-expanded={expanded}
        onPointerEnter={(event) => {
          if (event.pointerType === 'mouse') setHovered(true)
        }}
        onPointerMove={(event) => {
          if (event.pointerType === 'mouse' && !hovered) setHovered(true)
        }}
        onPointerLeave={(event) => {
          if (event.pointerType === 'mouse') setHovered(false)
        }}
      >
        <AnimatePresence onExitComplete={prune}>
          {toasts.map((item, index) => (
            <ToastItem
              key={item.id}
              toast={item}
              target={targets[index]!}
              expanded={expanded}
              front={index === 0}
              hidden={index >= visibleToasts}
              paused={paused}
              reduce={reduce}
              store={store}
              onMeasure={measure}
              onDragChange={setDragging}
              onTap={toggleTap}
              onHandOff={handOff}
            />
          ))}
        </AnimatePresence>
      </ol>
    </section>
  )
}
